import { useEffect, useState } from "react";
import { useLang } from "../i18n";
import { Icon } from "./Icon";
import { MUNICIPALITIES, REGIONS } from "../data/municipalities";
import { SHELTERS, SHELTERS_SOURCE, PREPS_URL } from "../data/shelters";
import { HOSPITALS_GEO } from "../data/hospitalsGeo";

const S = {
  preps: { es: "Ver refugios abiertos en tiempo real (PREPS)", en: "View shelters open in real time (PREPS)" },
  facilities: { es: "facilidades designadas", en: "designated facilities" },
  confirm: { es: "Confirme la apertura con su OMME antes de salir.", en: "Confirm opening with your OMME before leaving." },
  maps: { es: "Cómo llegar", en: "Directions" },
  liveNone: { es: "PREPS en vivo: no hay refugios abiertos en este momento.", en: "PREPS live: no shelters open right now." },
  liveOpen: { es: "REFUGIOS ABIERTOS AHORA (PREPS en vivo)", en: "SHELTERS OPEN NOW (PREPS live)" },
  openBadge: { es: "ABIERTO", en: "OPEN" },
  hasPolice: { es: "Policía municipal propia", en: "Own municipal police" },
  simOn: { es: "Ver simulacro de activación", en: "View activation drill" },
  simOff: { es: "Salir del simulacro", en: "Exit drill" },
  simTag: { es: "SIMULACRO — así lucirá durante un evento real", en: "DRILL — this is how it will look during a real event" },
  routeBtn: { es: "Ruta al refugio más cercano", en: "Route to nearest shelter" },
  routeHospBtn: { es: "Ruta al hospital más cercano", en: "Route to nearest hospital" },
  nearestHospLabel: { es: "Hospital más cercano a su ubicación", en: "Nearest hospital to your location" },
  locating: { es: "Localizando…", en: "Locating…" },
  routeError: { es: "No pudimos obtener su ubicación. Active el GPS o use el buscador de arriba.", en: "Could not get your location. Enable GPS or use the search box above." },
  nearestLabel: { es: "Refugio más cercano a su ubicación", en: "Nearest shelter to your location" },
  openRoute: { es: "Abrir ruta en Google Maps", en: "Open route in Google Maps" },
  approxKm: { es: "en línea recta", en: "as the crow flies" },
};

const t = (o, lang) => o[lang] ?? o.es;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const normName = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

function havKm(a, b, c, d) {
  const R = 6371, rad = (x) => (x * Math.PI) / 180;
  const h = Math.sin(rad(c - a) / 2) ** 2 + Math.cos(rad(a)) * Math.cos(rad(c)) * Math.sin(rad(d - b) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

const SHELTER_POINTS = Object.entries(SHELTERS).flatMap(([muni, items]) =>
  items.filter((s) => s.lat != null).map((s) => ({ ...s, muni }))
);

export function ShelterList({ region, query }) {
  const { lang } = useLang();
  const [live, setLive] = useState(null);
  const [demo, setDemo] = useState(false);
  const [route, setRoute] = useState(null);

  const findRoute = (type) => {
    if (!navigator.geolocation) { setRoute({ type, error: true }); return; }
    setRoute({ type, loading: true });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const points = type === "hospital"
          ? HOSPITALS_GEO.filter((h) => h.lat != null).map((h) => ({ ...h, facility: h.name, muni: h.town }))
          : SHELTER_POINTS;
        let best = null, bd = Infinity;
        for (const p of points) {
          const d = havKm(latitude, longitude, p.lat, p.lng);
          if (d < bd) { bd = d; best = p; }
        }
        if (!best) { setRoute({ type, error: true }); return; }
        const url = `https://www.google.com/maps/dir/?api=1&origin=${latitude},${longitude}&destination=${best.lat},${best.lng}`;
        setRoute({ type, best, d: bd, url });
        window.open(url, "_blank", "noopener");
      },
      () => setRoute({ type, error: true }),
      { timeout: 10000 }
    );
  };

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shelters/live${demo ? "?demo=1" : ""}`);
        if (r.ok && !dead) setLive(await r.json());
      } catch {}
    })();
    return () => { dead = true; };
  }, [demo]);

  const openTowns = new Set((live?.items || []).map((i) => normName(i.town || "")));
  const q = (query || "").trim().toLowerCase();
  const munis = MUNICIPALITIES.filter(
    (m) => (region === "all" || m.region === region) && (!q || m.name.toLowerCase().includes(q))
  );

  return (
    <div data-testid="shelter-list">
      <div className="mb-5 rounded-lg border-l-4 border-amber-600 border border-slate-300 bg-amber-50 p-4 sm:p-5">
        <p className="flex gap-2.5 text-sm text-amber-900 leading-relaxed">
          <Icon name="Info" className="w-5 h-5 shrink-0 text-amber-700" />
          <span>
            {t(SHELTERS_SOURCE, lang)} {t(S.confirm, lang)}
          </span>
        </p>
        <a
          data-testid="shelter-preps-link"
          href={PREPS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#1C2B4A] px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-700 transition-colors duration-200 active:scale-[0.98]"
        >
          <Icon name="ExternalLink" className="w-4 h-4" />
          {t(S.preps, lang)}
        </a>
        <button
          data-testid="simulacro-toggle"
          onClick={() => setDemo((d) => !d)}
          aria-pressed={demo}
          className={`mt-3 ml-0 sm:ml-2 inline-flex items-center gap-2 rounded-md border-2 px-4 py-2.5 text-sm font-bold transition-colors duration-200 active:scale-[0.98] ${
            demo ? "border-red-600 bg-red-600 text-white" : "border-amber-700 text-amber-800 hover:bg-amber-100"
          }`}
        >
          <Icon name="Siren" className="w-4 h-4" />
          {demo ? t(S.simOff, lang) : t(S.simOn, lang)}
        </button>
      </div>

      {live && (live.openCount === 0 ? (
        <div data-testid="preps-live-banner" className="mb-5 rounded-lg border border-green-700 bg-green-50 p-4 flex items-center gap-3">
          <Icon name="CheckCircle2" className="w-5 h-5 shrink-0 text-green-700" />
          <p className="text-sm font-semibold text-green-900">
            {t(S.liveNone, lang)}{" "}
            <span className="font-mono-pr text-xs text-green-700">
              PREPS · {new Date(live.fetchedAt).toLocaleString(lang === "es" ? "es-PR" : "en-US")}
              {live.source !== "preps" ? " · caché" : ""}
            </span>
          </p>
        </div>
      ) : (
        <div data-testid="preps-live-banner" className="mb-5 rounded-lg border-l-4 border-red-600 border border-slate-300 bg-red-50 p-4 sm:p-5">
          {live.source === "demo" && (
            <p data-testid="simulacro-tag" className="mb-2 inline-flex rounded-full bg-amber-600 px-3 py-1 font-mono-pr text-[11px] font-bold uppercase tracking-[0.15em] text-white">
              {t(S.simTag, lang)}
            </p>
          )}
          <p className="flex items-center gap-2.5 font-bold text-red-800">
            <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse-dot" />
            <span data-testid="preps-open-count">{live.openCount} {t(S.liveOpen, lang)}</span>
          </p>
          <ul className="mt-2.5 space-y-1">
            {live.items.map((it, i) => (
              <li key={i} data-testid={`preps-open-item-${i}`} className="text-sm text-red-900">
                <b>{it.name}</b>{it.town ? ` — ${it.town}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="mb-5">
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="route-nearest-btn"
            onClick={() => findRoute("shelter")}
            disabled={route?.loading}
            className="inline-flex items-center gap-2 rounded-md bg-[#1C2B4A] px-5 py-3 text-sm font-bold text-white hover:bg-amber-700 transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
          >
            <Icon name="Route" className="w-4 h-4" />
            {route?.loading && route?.type === "shelter" ? t(S.locating, lang) : t(S.routeBtn, lang)}
          </button>
          <button
            data-testid="route-hospital-btn"
            onClick={() => findRoute("hospital")}
            disabled={route?.loading}
            className="inline-flex items-center gap-2 rounded-md border-2 border-blue-700 bg-white px-5 py-3 text-sm font-bold text-blue-800 hover:bg-blue-700 hover:text-white transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
          >
            <Icon name="Cross" className="w-4 h-4" />
            {route?.loading && route?.type === "hospital" ? t(S.locating, lang) : t(S.routeHospBtn, lang)}
          </button>
        </div>
        {route?.error && (
          <p data-testid="route-error" className="mt-2 text-sm font-semibold text-red-700">{t(S.routeError, lang)}</p>
        )}
        {route?.best && (
          <div data-testid="route-result" className="mt-3 rounded-md border border-green-700 bg-green-50 p-4 max-w-xl">
            <p className="font-mono-pr text-[11px] font-bold uppercase tracking-[0.15em] text-green-800">
              {route.type === "hospital" ? t(S.nearestHospLabel, lang) : t(S.nearestLabel, lang)}
            </p>
            <p className="mt-1.5 font-bold text-slate-900">{route.best.facility} — {route.best.muni}</p>
            <p className="text-sm text-slate-600">~{route.d.toFixed(1)} km {t(S.approxKm, lang)}</p>
            <a
              data-testid="route-open-maps"
              href={route.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-amber-700 hover:text-amber-900"
            >
              <Icon name="ExternalLink" className="w-4 h-4" /> {t(S.openRoute, lang)}
            </a>
          </div>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {munis.map((m) => {
          const items = SHELTERS[m.name] || [];
          return (
            <details
              key={m.name}
              data-testid={`shelter-muni-${slug(m.name)}`}
              className="group rounded-lg border border-slate-300 bg-white open:border-amber-500 transition-colors duration-200"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 sm:p-5">
                <span className="flex items-center gap-2.5 min-w-0">
                  <Icon name="MapPin" className="w-5 h-5 shrink-0 text-amber-700" />
                  <span className="font-display text-base font-bold text-slate-900 truncate">{m.name}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 shrink-0">
                    {t(REGIONS[m.region], lang)}
                  </span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {openTowns.has(normName(m.name)) && (
                    <span data-testid={`shelter-open-${slug(m.name)}`} className="rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white animate-pulse-dot">
                      {t(S.openBadge, lang)}
                    </span>
                  )}
                  <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-mono-pr text-[11px] font-bold text-amber-800">
                    {items.length} {t(S.facilities, lang)}
                  </span>
                  <Icon name="ChevronRight" className="w-4 h-4 text-slate-400 transition-transform duration-200 group-open:rotate-90" />
                </span>
              </summary>
              <div className="border-t border-slate-200 px-4 sm:px-5 py-4">
                <ul className="space-y-3.5 mb-4">
                  {items.map((s, i) => (
                    <li key={i} data-testid={`shelter-item-${slug(m.name)}-${i}`} className="text-sm">
                      <p className="font-bold text-slate-900 leading-snug">{s.facility}</p>
                      {s.address && <p className="text-slate-600 leading-snug mt-0.5">{s.address}</p>}
                      <a
                        data-testid={`shelter-maps-${slug(m.name)}-${i}`}
                        href={s.maps}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-900"
                      >
                        <Icon name="ExternalLink" className="w-3.5 h-3.5" /> {t(S.maps, lang)}
                      </a>
                    </li>
                  ))}
                </ul>
                <a
                  data-testid={`shelter-omme-${slug(m.name)}`}
                  href={`tel:${m.ommeTel}`}
                  className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-700 transition-colors duration-200 active:scale-[0.97]"
                >
                  <Icon name="Phone" className="w-4 h-4" /> OMME {m.omme}
                </a>
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}
